import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Loader2, ArrowRight } from 'lucide-react'
import { useAuthStore, AziendaInfo } from '../core/stores/authStore'

export default function SelectAziendaPage() {
  const navigate = useNavigate()
  const { user, aziende, selectAzienda, isLoading, error, isAuthenticated } = useAuthStore()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Se non autenticato, redirect a login
  if (!isAuthenticated || !user) {
    navigate('/')
    return null
  }

  // Se ha gia' un'azienda selezionata, vai all'app
  if (user.azienda_id) {
    navigate('/app')
    return null
  }

  // Se nessuna azienda disponibile, vai all'app (backward compat)
  if (aziende.length === 0) {
    navigate('/app')
    return null
  }

  const handleSelect = async (azienda: AziendaInfo) => {
    setSelectedId(azienda.id)
    const success = await selectAzienda(azienda.id)
    if (success) {
      navigate('/app')
    } else {
      setSelectedId(null)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 border border-gray-200">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Seleziona Azienda</h1>
          <p className="text-sm text-gray-500 mt-2">
            Ciao <span className="font-medium">{user.username}</span>, su quale azienda vuoi lavorare?
          </p>
        </div>

        {/* Lista aziende */}
        <div className="space-y-3">
          {aziende.map((az) => (
            <button
              key={az.id}
              onClick={() => handleSelect(az)}
              disabled={isLoading}
              className={`
                w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left
                ${selectedId === az.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }
                ${isLoading && selectedId !== az.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Logo o iniziale */}
              <div className="w-12 h-12 rounded-lg bg-primary-600 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-white">
                  {az.codice.substring(0, 2).toUpperCase()}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{az.nome}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ruolo: <span className="font-medium capitalize">{az.role}</span>
                </p>
              </div>

              {/* Freccia o loader */}
              <div className="flex-shrink-0">
                {isLoading && selectedId === az.id ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                ) : (
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Errore */}
        {error && (
          <div className="mt-4 bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg border border-red-100">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}
